/**
 * The app on the device (part 40a): the update notice (table U), the Home Screen screen and the browser banner on iOS
 * (table H), and the settings' App section: whether the app works offline (R′) and "Install" where the browser offers it
 * (table P). Each says what it says in the page's flow, never over the text.
 */
import { createMemo } from 'solid-js';
import type { UpdateState } from '../pwa/controller.ts';
import { Announcer } from './announce.ts';
import { el, when } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { Section } from './setting-rows.ts';
import type { Shell } from './shell.ts';
import Banner from '../ui/Banner.ts';
import Button from '../ui/Button.ts';
import EmptyState from '../ui/EmptyState.ts';
import { component } from '../ui/solid.ts';

/** What the update notice says (U4–U6). */
export function updateText(shell: Shell, state: UpdateState): string {
  const { t } = shell;
  switch (state) {
    case 'none':
      return '';
    case 'ready':
      return t('update.ready');
    case 'updating':
      return t('update.updating');
    case 'failed':
      return t('update.failed');
  }
}

/** The update notice's tone: news, work under way, or a stop (the words always say which). */
function updateTone(state: UpdateState): 'info' | 'neutral' | 'danger' {
  return state === 'failed' ? 'danger' : state === 'updating' ? 'neutral' : 'info';
}

/**
 * U4–U6: the update notice, shown while an update waits, runs or has stopped; "Update now" or "Try again". With
 * `announce`, its announcer is in the page from the start (part 11c, A13); the workspace's notice is announced by the
 * workspace's announcers instead. PLAN.md §3, step 8: a Banner, as the job statuses are (job-status.ts); its region
 * keeps the name the page's landmarks list.
 */
export function UpdateNotice(shell: Shell, announce = true): Child {
  const { t, pwa } = shell;
  return [
    announce ? Announcer('update', () => updateText(shell, pwa.update())) : null,
    when(
      () => pwa.update() !== 'none',
      () => {
        // One button per state, made once as the state changes (the Banner reads its actions more than once).
        const action = createMemo(() => {
          const state = pwa.update();
          return state === 'ready' || state === 'failed'
            ? component(Button, {
                // Secondary: the page under it keeps its one primary (Home's Continue writing).
                size: 'sm',
                onClick: () => {
                  pwa.applyUpdate();
                },
                children: state === 'ready' ? t('update.apply') : t('update.retry'),
              })
            : undefined;
        });
        return el(
          'section',
          { class: 'job-status update-status', 'aria-label': t('update.label') },
          component(Banner, {
            get tone() {
              return updateTone(pwa.update());
            },
            children: el('p', {}, () => updateText(shell, pwa.update())),
            get actions() {
              return action();
            },
          }),
        );
      },
    ),
  ];
}

/**
 * H2: the Home Screen screen, before the workspace: why, how, and "Keep writing in the browser" (H3). PLAN.md §3, step
 * 8: the handoff's iOS guide (`test-build?state=ios`) as an EmptyState: its icon, title, words, steps and one button.
 */
export function HomeScreenAsk(shell: Shell): Child {
  const { t, homeScreen } = shell;
  return el(
    'main',
    { class: 'screen home-screen', 'aria-labelledby': 'screen-heading' },
    component(EmptyState, {
      icon: 'plusSquare',
      title: t('homeScreen.title'),
      headingLevel: 1,
      headingId: 'screen-heading',
      headingFocusable: true,
      children: [
        el('p', { id: 'screen-body' }, t('homeScreen.body')),
        el(
          'div',
          { class: 'home-screen-how' },
          // The handoff shows the steps with no heading; the heading still names the list for a screen reader.
          el('h2', { id: 'home-screen-steps', class: 'visually-hidden' }, t('homeScreen.steps')),
          el('ol', { 'aria-labelledby': 'home-screen-steps' }, el('li', {}, t('homeScreen.step1')), el('li', {}, t('homeScreen.step2')), el('li', {}, t('homeScreen.step3'))),
        ),
      ],
      actions: component(Button, {
        variant: 'primary',
        // Part 11c, deep-fix r5: read with the screen's message as it takes the focus.
        'aria-describedby': 'screen-body',
        onClick: () => {
          homeScreen.keepHere();
          shell.focusEditor();
        },
        children: t('homeScreen.keep'),
      }),
    }),
  );
}

/** H3, H4: the banner over the workspace's toolbar while writing in the browser on iOS, in focus mode too. */
export function BrowserBanner(shell: Shell): Child {
  return when(
    () => shell.homeScreen.state() === 'warn',
    () => el('div', { class: 'home-screen-banner', role: 'note' }, component(Banner, { strip: true, tone: 'warn', children: shell.t('homeScreen.banner') })),
  );
}

/** What the App section says about offline use (R′1–R′3). */
export function offlineText(shell: Shell): string {
  const { t } = shell;
  switch (shell.pwa.offline()) {
    case 'unavailable':
      return t('offline.unavailable');
    case 'preparing':
      return t('offline.preparing');
    case 'ready':
      return t('offline.ready');
  }
}

/** The settings' App section: offline use (R′) and "Install" while the browser offers it (P1, P2). */
export function AppSettings(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, installer } = shell;
  return Section(
    'settings-app',
    t('settings.app'),
    heading,
    el('p', { class: 'offline-status' }, () => offlineText(shell)),
    when(installer.available, () =>
      el(
        'div',
        { class: 'field' },
        component(Button, {
          'aria-describedby': 'install-hint',
          onClick: () => {
            installer.install();
          },
          children: t('install.button'),
        }),
        el('p', { id: 'install-hint', class: 'hint' }, t('install.hint')),
      ),
    ),
  );
}
